import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import axios from 'axios';
import {
    IconButton,
    Paper,
    Tooltip,
} from '@material-ui/core';
import {
    Table,
    Checkbox,
} from 'rsuite';
import "rsuite/dist/rsuite.min.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv } from '@fortawesome/pro-light-svg-icons';
import FilterSelectionBar from '../../FilterSelectionBar';
import CustomToolTip from '../../../../common/components/UI/ToolTip/ToolTip';
import {
    updateSpinnerShow,
    updateSnackbar
} from '../actions';
import './WorkplanAudits.scss';
import {
    downloadContentInFile,
    handleSort,
    searchableAudit
} from './WorkPlanAudits.service.js';
import { WorkplanCurrentStatus } from '../../../Enums/Status';
import { currentHttpOptions } from '../../../../common/utilities/utilitiesFunctions';
import { handleAPIErrors } from '../../../auxilaries'
import { useWindowDimensions } from '../../hooks/index';
import DateFormatService from '../../../services/DateFormatService/DateFormatService.js';
import { UkgDateFormat } from '../../../Enums/UkgDateFormat.js';


const { Column, HeaderCell, Cell } = Table;

function WorkPlanAudits({ updateSpinnerShow, updateSnackbar, ...props }) {
    const {t: translate} = props;
    const t = (prop) => translate(`workplanManager:workPlanAudits.${prop}`);
    const [sortValues, setSortValues] = useState({ sortColumn: '', sortType: 'asc' });
    const [checkedKeys, setCheckedKeys] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [statusFilterSelected, setStatusFilterSelected] = useState([]);
    const [filteredAudits, setFilteredAudits] = useState([]);
    const [allAudits, setAllAudits] = useState([]);
    const ref = useRef(null);
    const height = useWindowDimensions().height - 200;

    useEffect(() => {
        const getManagerAudits = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_DOTNET_URL}/api/v1/manager/workplan/GetAllManagerAudits`, currentHttpOptions());
                const audits = response.data;
                setAllAudits(audits);
                setFilteredAudits(audits)
            } catch (error) {
                updateSnackbar({
                    typeSnackbar: 'error',
                    messageSnackbar: handleAPIErrors(error.response),
                    openSnackbar: true,
                })
            } finally {
                updateSpinnerShow(false);
            }
        };
        updateSpinnerShow(true);
        getManagerAudits();
    }, []);

    useEffect(() => {
        handleSort(filteredAudits, setFilteredAudits, sortValues.sortColumn, sortValues.sortType);
    }, [JSON.stringify(sortValues)]);

    useEffect(() => {
        handleFilter();
    }, [searchValue, JSON.stringify(statusFilterSelected), JSON.stringify(allAudits)]);

    const handleCheck = (id, checked) => {
        if (checked === false) {
            setCheckedKeys([]);
            return;
        }
        setCheckedKeys([id]);
    };

    const handleFilter = () => {
        let filteredAudits = allAudits;
        if (!searchValue && !statusFilterSelected.length) {
            setFilteredAudits(filteredAudits);
        }

        if (searchValue) {
            filteredAudits = filteredAudits.filter((audit) => searchableAudit(audit).includes(searchValue.toLocaleLowerCase()));
        }
        if (statusFilterSelected.length) {
            filteredAudits = filteredAudits.filter((audit) => statusFilterSelected.includes(audit.currentStatus.value));
        }
        setFilteredAudits(filteredAudits);
    }

    const downloadReportAsCsv = async (profileId) => {
        try {
            updateSpinnerShow(true);
            const response = await axios.get(`${process.env.REACT_APP_DOTNET_URL}/api/v1/manager/workplan/GetManagerAuditCsv/${profileId}`, currentHttpOptions());
            downloadContentInFile('ManagerAuditReport', 'csv', response.data);
        } catch (error) {
            updateSnackbar({
                typeSnackbar: 'error',
                messageSnackbar: handleAPIErrors(error.response),
                openSnackbar: true,
            })
        } finally {
            updateSpinnerShow(false);
        }
    };

    return (<>
        <div className="wp-audits-full-page">
            <div
                className="header-container"
            >
                <FilterSelectionBar
                    setStatusFilterSelected={setStatusFilterSelected}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    allStatus={Object.values(WorkplanCurrentStatus)}
                />
                <div className="download-icon">
                    <Tooltip
                        title={t('downloadToCsv')}
                        arrow
                        placement="bottom-end"
                    >
                        <IconButton
                            className="header-icon-button"
                            onClick={() => downloadReportAsCsv(checkedKeys)}
                        >
                            <FontAwesomeIcon
                                icon={faFileCsv}
                            />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
            <Paper
                elevation={2}
                className="wp-audits-table-container"
                ref={ref}
            >
                <Table
                    virtualized
                    sortColumn={sortValues.sortColumn}
                    sortType={sortValues.sortType}
                    onSortColumn={(sortColumn, sortType) => setSortValues({ sortColumn, sortType })}
                    height={height}
                    data={filteredAudits}
                    id="wp-audits"
                    className="wp-audits-table"
                    rowKey={"workplanId"}
                    shouldUpdateScroll={false}
                >
                    <Column
                        width={100}
                        align="center"
                    >
                        <HeaderCell />
                        <Cell>
                            {(rowData) => (
                                <div style={{ lineHeight: '46px' }}>
                                    <Checkbox
                                        value={rowData.workplanId}
                                        inline
                                        onChange={handleCheck}
                                        checked={checkedKeys.some((item) => item === rowData.workplanId)}
                                    />
                                </div>
                            )}
                        </Cell>
                    </Column>
                    <Column
                        flexGrow={3}
                        sortable
                        key="name"
                    >
                        <HeaderCell>{t("columns.name")}</HeaderCell>
                        <Cell
                            dataKey="name"
                        >
                            {(rowData) => rowData.name}
                        </Cell>
                    </Column>
                    <Column
                        flexGrow={3}
                        sortable
                    >
                        <HeaderCell>{t("columns.description")}</HeaderCell>
                        <Cell
                            dataKey="description"
                        >
                            {(rowData) => (<CustomToolTip
                                enterDelay={500}
                                leaveDelay={500}
                                titletext={rowData.description}
                                jsx={<div className="wp-audits-tooltip">{rowData.description}</div>}
                                placement="bottom-start"
                            />)}
                        </Cell>
                    </Column>
                    <Column
                        flexGrow={2}
                        sortable
                    >
                        <HeaderCell>{t("columns.status")}</HeaderCell>
                        <Cell
                            dataKey="currentStatus"
                        >
                            {(rowData) => rowData.currentStatus.label}
                        </Cell>
                    </Column>
                    <Column
                        flexGrow={2}
                        sortable
                    >
                        <HeaderCell>{t("columns.statusDate")}</HeaderCell>
                        <Cell
                            dataKey="currentStatusEffectiveDate"
                        >
                            {(rowData) => DateFormatService.formatDateOrEmpty(rowData.currentStatus.effectiveDate, UkgDateFormat.LongDate)}
                        </Cell>
                    </Column>

                </Table>
            </Paper>
        </div>
    </>);
}

const mapDispatchToProps = {
    updateSpinnerShow,
    updateSnackbar,
}

export default connect(() => ({ }), mapDispatchToProps)(translate('workplanManager')(WorkPlanAudits));